// src/components/mitchRoom/revenueAnalytics/CustomersTable.js
import React, { useState, useMemo } from 'react';
import { ArrowUp, ArrowDown, Search } from 'lucide-react';
import { Badge } from 'components/ui/badge';
import { Input } from 'components/ui/input';
import { Skeleton } from 'components/ui/skeleton';
import { cn } from 'lib/utils';
import { formatCurrency } from 'utils/format';

const getStatusVariant = (status) => {
  switch (status?.toLowerCase()) {
    case 'active':
      return 'success';
    case 'canceled':
    case 'cancelled':
      return 'destructive';
    case 'paused':
      return 'warning';
    case 'expired':
    default:
      return 'outline';
  }
};

const HEADER_TH =
  'sticky top-0 z-[3] whitespace-nowrap border-b border-borderGrey bg-white p-2 text-left text-token-md font-semibold';

function SortableTh({ field, orderBy, order, onSort, children, className }) {
  const active = orderBy === field;
  return (
    <th
      onClick={() => onSort(field)}
      aria-sort={active ? (order === 'asc' ? 'ascending' : 'descending') : 'none'}
      className={cn(HEADER_TH, 'cursor-pointer select-none hover:bg-neutral-50', className)}
    >
      <span className="inline-flex items-center gap-1">
        {children}
        {active &&
          (order === 'asc' ? (
            <ArrowUp size={12} aria-hidden="true" />
          ) : (
            <ArrowDown size={12} aria-hidden="true" />
          ))}
      </span>
    </th>
  );
}

export default function CustomersTable({ customers, loading }) {
  const [orderBy, setOrderBy] = useState('total_revenue_gbp');
  const [order, setOrder] = useState('desc');
  const [search, setSearch] = useState('');

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const filteredAndSorted = useMemo(() => {
    let result = [...(customers || [])];

    // Filter by search
    if (search) {
      const searchLower = search.toLowerCase();
      result = result.filter(
        (c) =>
          c.email?.toLowerCase().includes(searchLower) ||
          c.name?.toLowerCase().includes(searchLower) ||
          c.referred_by?.toLowerCase().includes(searchLower) ||
          String(c.wp_id).includes(search)
      );
    }

    // Sort
    result.sort((a, b) => {
      let aVal = a[orderBy];
      let bVal = b[orderBy];

      // Handle nulls
      if (aVal === null || aVal === undefined) aVal = 0;
      if (bVal === null || bVal === undefined) bVal = 0;

      if (order === 'asc') {
        return aVal > bVal ? 1 : -1;
      }
      return aVal < bVal ? 1 : -1;
    });

    return result;
  }, [customers, search, orderBy, order]);

  if (loading && (!customers || customers.length === 0)) {
    return (
      <div className="rounded-token-large border border-borderGrey bg-white p-4">
        <h3 className="mb-3 text-token-xl font-semibold">Top Customers</h3>
        <div className="flex flex-col gap-2">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-token-large border border-borderGrey bg-white">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3 border-b border-borderGrey px-4 py-3">
        <h3 className="text-token-xl font-semibold">Top Customers</h3>
        {(customers?.length ?? 0) >= 100 && (
          <span className="text-token-sm text-muted-foreground">
            Showing top 100 customers by revenue
          </span>
        )}
        <div className="relative ml-auto">
          <Search
            size={14}
            className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground"
            aria-hidden="true"
          />
          <Input
            placeholder="Search name, email, WP ID..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-[240px] pl-8 text-sm"
          />
        </div>
      </div>

      {filteredAndSorted.length === 0 ? (
        <p className="p-6 text-center text-token-md text-muted-foreground">No customers found</p>
      ) : (
        <div className="max-h-[500px] overflow-auto">
          <table className="w-full border-separate border-spacing-0 text-sm">
            <thead>
              <tr>
                <th className={HEADER_TH}>#</th>
                <th className={HEADER_TH}>Customer</th>
                <SortableTh
                  field="total_revenue_gbp"
                  orderBy={orderBy}
                  order={order}
                  onSort={handleSort}
                >
                  Revenue
                </SortableTh>
                <SortableTh field="total_orders" orderBy={orderBy} order={order} onSort={handleSort}>
                  Orders
                </SortableTh>
                <th className={HEADER_TH}>First Order</th>
                <th className={HEADER_TH}>Status</th>
                <th className={HEADER_TH}>Type</th>
                <th className={HEADER_TH}>Referred By</th>
              </tr>
            </thead>
            <tbody>
              {filteredAndSorted.map((customer, index) => (
                <tr key={customer.wp_id} className="hover:bg-neutral-50">
                  <td className="border-b border-borderGrey p-2 text-muted-foreground">
                    {index + 1}
                  </td>
                  <td className="border-b border-borderGrey p-2">
                    <p className="font-medium">{customer.name || 'Unknown'}</p>
                    <p className="text-token-sm text-muted-foreground">
                      {customer.email || `WP ID: ${customer.wp_id}`}
                    </p>
                  </td>
                  <td className="border-b border-borderGrey p-2 font-semibold tabular-nums">
                    {customer.total_revenue_gbp === null || customer.total_revenue_gbp === undefined
                      ? '-'
                      : formatCurrency(customer.total_revenue_gbp)}
                  </td>
                  <td className="border-b border-borderGrey p-2 tabular-nums">
                    {customer.total_orders}
                  </td>
                  <td className="whitespace-nowrap border-b border-borderGrey p-2">
                    {customer.first_order_date
                      ? new Date(customer.first_order_date).toLocaleDateString('en-GB', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric',
                        })
                      : '-'}
                  </td>
                  <td className="border-b border-borderGrey p-2">
                    {customer.current_status ? (
                      <Badge variant={getStatusVariant(customer.current_status)}>
                        {customer.current_status}
                      </Badge>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td className="border-b border-borderGrey p-2 text-muted-foreground">
                    {customer.subscription_type || '-'}
                  </td>
                  <td className="whitespace-nowrap border-b border-borderGrey p-2">
                    {customer.referred_by ? (
                      <Badge variant="info" title={`Referrer WP ID: ${customer.referrer_wp_id}`}>
                        {customer.referred_by}
                      </Badge>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
